/* eslint-disable react/no-unused-prop-types */
import React, { type FC, type JSX, useEffect, useMemo, useRef, useState } from 'react';
import SliderOpenx, { type SliderThumbProps } from '@/app/components/search/components/Slider/index.tsx';
import { debounce } from '@/utils/debounce.ts';
import Label from './Label';
import SliderModel from './model';
import Value from './Value';
import { SliderContainer, SliderContainerStyled } from './styled';
import type { ControlProps, Target } from '../../index';
import { sumClasses } from '../../../../utils/misc';

const COMMIT_FALLBACK_MS = 50;
const KEYBOARD_SETTLE_MS = 500;

export interface SliderProps extends ControlProps, SliderThumbProps {
  value: {
    min: number;
    max: number;
  };
  default: {
    min: number;
    max: number;
  };
  minAllowed: number;
  maxAllowed: number;
  bulletSize: number;
  roundTo: number;
  hideMin?: boolean;
  onUpdate: (e: Target) => Promise<void>;
  width?: number;
  name: string;
  diagram?: JSX.Element;
}

const Slider: FC<SliderProps> = (props) => {
  const { minAllowed, maxAllowed, onUpdate, name, value, label, valueLabel, className, diagram, disabled } =
    new SliderModel(props);

  const innerRef = useRef<HTMLDivElement>(null);
  const { min, max } = value;

  // Single state for current slider values (either from props or during drag)
  const [currentValues, setCurrentValues] = useState({ min, max });

  // Radix onValueCommit sometimes doesn't fire on Chrome.
  // The design-system workaround (onLostPointerCapture) suffers from stale closures.
  // This ref stores the last drag value so onPointerUp can commit it if Radix didn't.
  const uncommittedValueRef = useRef<{ min: number; max: number } | null>(null);

  // Update current values when props change (but not during drag operations)
  useEffect(() => {
    if (uncommittedValueRef.current) return;
    setCurrentValues({ min, max });
  }, [min, max]);

  const containerClassName = sumClasses(['container-field oss-ui-slider', className || '']);

  const handleChange = (changedValues: number[] | number) => {
    const showOnlyMax = !Array.isArray(changedValues);
    const _min = showOnlyMax ? 0 : changedValues[0];
    const _max = showOnlyMax ? changedValues : changedValues[1];

    setCurrentValues({ min: _min, max: _max });
    uncommittedValueRef.current = { min: _min, max: _max };
  };

  const commitPending = () => {
    const pending = uncommittedValueRef.current;
    if (!pending) return;
    uncommittedValueRef.current = null;
    if (pending.min !== min || pending.max !== max) {
      onUpdate({ target: { name, value: pending } });
    }
  };
  const commitPendingRef = useRef(commitPending);
  commitPendingRef.current = commitPending;

  // Radix commits on every arrow key, so a run of them settles into one update.
  const settle = useMemo(() => debounce(() => commitPendingRef.current(), KEYBOARD_SETTLE_MS), []);
  useEffect(() => () => settle.cancel(), [settle]);

  const handleCommittedChange = (changedValues: number[] | number) => {
    const showOnlyMax = !Array.isArray(changedValues);
    const _min = showOnlyMax ? 0 : changedValues[0];
    const _max = showOnlyMax ? changedValues : changedValues[1];

    uncommittedValueRef.current = { min: _min, max: _max };
    settle();
  };

  const commitIfMissed = () => {
    settle.cancel();
    setTimeout(() => commitPendingRef.current(), COMMIT_FALLBACK_MS);
  };

  // Apply can follow the last key straight away, so leaving the slider skips the settle.
  const commitOnLeave = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    settle.cancel();
    commitPendingRef.current();
  };

  return (
    <SliderContainer className={containerClassName} disabled={!!disabled} data-testid="slider">
      {label ? <Label label={label} hasDiagram={!!diagram} /> : null}
      <SliderContainerStyled
        className="oss-ui-slider-container"
        ref={innerRef}
        onPointerUp={commitIfMissed}
        onBlur={commitOnLeave}
      >
        {diagram}
        <SliderOpenx
          value={{
            min: currentValues.min,
            minAllowed,
            max: currentValues.max,
            maxAllowed,
            disabled: !!disabled,
          }}
          disabled={!!disabled}
          handleChange={handleChange}
          handleCommittedChange={handleCommittedChange}
          formatter={(f) => `${f}`}
          hideMin={minAllowed === maxAllowed}
          thumbNames={props.thumbNames}
          valueText={props.valueText}
        />
      </SliderContainerStyled>
      <Value
        minValue={currentValues.min}
        maxValue={currentValues.max}
        valueLabel={valueLabel}
        minAllowed={minAllowed}
        maxAllowed={maxAllowed}
      />
    </SliderContainer>
  );
};

export default Slider;
